import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { BdsTableCell, BdsTableRow, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import { trackEvent } from '../../../../api/Billing/Tracking';
import { getMonthTwoDigits, toUTCDateString } from '../../../../utils/date';
import { PageHeaderComponent } from '../../components/Metrics/PageHeader/PageHeaderComponent';
import { AgentConsumptionPaper } from '../styles';
import AgentConsumptionContext from '../../../../contexts/AgentConsumptionContext';
import { origin, REPORT_PERIOD_SELECT } from '../../../../shared/SegmentEvents';
import TableWithPagination from '../../../../components/TableWithPagination/TableWithPagination';
import { useSortedPaginatedData } from '../../../../hooks/useSortedPaginatedData';
import { DEFAULT_PAGE_OPTIONS, COLUMN_WIDTHS_5 as COLUMN_WIDTHS } from '../../../../utils/paginationUtils';

const COLUMN_KEYS = ['agentName', 'extraCharge', 'totalTickets', 'firstTicketDate', 'lastTicketDate'];

export interface AgentConsumptionMonthlyUsageProps {
  isDetailedView?: boolean;
}

const AgentConsumptionMonthlyUsageComponent: FC<AgentConsumptionMonthlyUsageProps> = ({
  isDetailedView = false,
}) => {
  const {
    dateToSearch,
    periodOptions,
    content,
    tenant,
    loggedUser,
    setHeaderContent,
    trackingProps,
    getPlanStatus,
    setDateToSearch,
    billingAgentConsumptionApi,
    monthlyAgentConsumptionDataState,
    setMonthlyAgentConsumptionDataState,
  } = useContext(AgentConsumptionContext);
  const [isLoading, setLoading] = useState(false);

  const { itemsPerPage, sortConfig, sortedData, paginatedData, handlePaginationChange, handleItemsPerPageChange, handleSortChange } =
    useSortedPaginatedData({ data: monthlyAgentConsumptionDataState });

  useEffect(() => {
    setHeaderContent({
      redirect: '/data-consumption',
      text: content.formatString(content.header, tenant.info.name),
    });
    return () => setHeaderContent({ redirect: null });
  }, []);

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
        const data = await billingAgentConsumptionApi.getMonthlyAgentConsumption(
          tenant.info.id,
          monthString,
          -1,
        );
        setMonthlyAgentConsumptionDataState(
          data.map((obj) => ({
            ...obj,
            extraCharge: obj.extraCharge ? content.monthly.extraCharge[0] : content.monthly.extraCharge[1],
            firstTicketDate: toUTCDateString(new Date(obj.firstTicketDate), content.locale),
            lastTicketDate: toUTCDateString(new Date(obj.lastTicketDate), content.locale),
          })),
        );
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [dateToSearch]);

  const tableRows = useMemo(
    () =>
      paginatedData.length > 0
        ? paginatedData.map((item) => (
            <BdsTableRow key={item.agentEmail}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.agentName}
                </BdsTypo>
                <BdsTypo variant="fs-12" margin={false}>
                  {item.agentEmail}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.extraCharge}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.totalTickets.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.firstTicketDate}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.lastTicketDate}
                </BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedData, content.locale],
  );

  const periodOptionChangeEvent = async (selectedOption) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    const planStatus = await getPlanStatus();
    trackEvent(REPORT_PERIOD_SELECT, tenant, loggedUser, {
      ...trackingProps,
      origin: origin.AGENTCONSUMPTIONLIST,
      date: dateToRetrieveData,
      planStatus,
    });
  };

  return (
    <>
      {isDetailedView && (
        <PageHeaderComponent
          title={content.monthly.title}
          dateToSearch={dateToSearch}
          periodOptions={periodOptions}
          periodOptionChangeEvent={periodOptionChangeEvent}
        />
      )}
      <AgentConsumptionPaper>
        {!isDetailedView && (
          <BdsTypo variant="fs-24" bold="bold" margin={false}>
            {content.monthly.title}
          </BdsTypo>
        )}
        <BdsTypo variant="fs-14" margin={false}>
          {content.monthly.subtitle}
        </BdsTypo>
        <TableWithPagination
          options={{
            heading: [
              content.monthly.columnAgentId,
              content.monthly.columnExtraCharge,
              content.monthly.columnNumTickets,
              content.monthly.columnFirstRepliedTicket,
              content.monthly.columnLastRepliedTicket,
            ],
            columnKeys: COLUMN_KEYS,
            columnWidths: COLUMN_WIDTHS,
            sortConfig,
            onSortChange: handleSortChange,
            itemsPerPageList: DEFAULT_PAGE_OPTIONS,
            itemsPerPage,
            numberOfItems: isLoading ? itemsPerPage : sortedData.length,
            isLoading,
            hasEmptyState: !isLoading && sortedData.length === 0,
            emptyStateSubtitle: '',
            isAutoHeight: true,
          }}
          onPaginationChange={handlePaginationChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          {tableRows}
        </TableWithPagination>
      </AgentConsumptionPaper>
    </>
  );
};

export default AgentConsumptionMonthlyUsageComponent;
